/**
 * useInvitationLink Hook
 *
 * Hook for validating and managing invitation links in the end user flow
 */

import { useState, useEffect, useCallback } from 'react';
import { advisoryService } from 'services/advisoryService';
import { InvitationLink, InvitationLinkStatus, LeadRequest } from 'types/leadRequestsTypes';
import { useToast } from 'hooks/useToast';
import { useUser } from 'global/UserContext';

interface UseInvitationLinkReturn {
  linkData: InvitationLink | null;
  requestData: LeadRequest | null;
  loading: boolean;
  error: string | null;
  validating: boolean;
  submitting: boolean;
  validateLink: (linkId: string) => Promise<boolean>;
  submitResponse: (formResponses: Record<string, any>) => Promise<boolean>;
}

export const useInvitationLink = (linkId?: string): UseInvitationLinkReturn => {
  const [linkData, setLinkData] = useState<InvitationLink | null>(null);
  const [requestData, setRequestData] = useState<LeadRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toastError, toastSuccess } = useToast();
  const { user } = useUser();

  const validateLink = useCallback(
    async (linkId: string): Promise<boolean> => {
      setValidating(true);
      setError(null);

      try {
        // Get invitation link
        const link = await advisoryService.getInvitationLink(linkId);
        setLinkData(link);

        // Check link status
        if (link.status === InvitationLinkStatus.EXPIRED) {
          setError('This invitation link has expired');
          toastError('This invitation link has expired');
          return false;
        }

        if (link.status === InvitationLinkStatus.USED) {
          setError('This invitation link has already been used');
          toastError('This invitation link has already been used');
          return false;
        }

        // Get the associated lead request
        const request = await advisoryService.getLeadRequest(link.lead_request_id);
        setRequestData(request);

        return true;
      } catch (err: any) {
        const errorMessage = err.response?.status === 404
          ? 'Invalid invitation link'
          : err.response?.data?.message || 'Failed to validate invitation link';
        setError(errorMessage);
        toastError(errorMessage);
        console.error('Error validating invitation link:', err);
        return false;
      } finally {
        setValidating(false);
      }
    },
    [toastError]
  );

  const submitResponse = useCallback(
    async (formResponses: Record<string, any>): Promise<boolean> => {
      if (!linkData || !requestData || !user?.id) {
        toastError('Missing required data for submission');
        return false;
      }

      setSubmitting(true);
      setError(null);

      try {
        // Update the lead request with end user's response
        await advisoryService.updateLeadRequest(requestData.id, {
          lead_user_id: user.id,
          form_responses: {
            ...(requestData.form_responses || {}),
            end_user_response: formResponses,
          },
        });

        // Update invitation link status to USED
        await advisoryService.updateInvitationLink(linkData.id, {
          status: InvitationLinkStatus.USED,
          lead_user_id: user.id,
        });

        toastSuccess('Response submitted successfully!');
        return true;
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to submit response';
        setError(errorMessage);
        toastError(errorMessage);
        console.error('Error submitting response:', err);
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [linkData, requestData, user, toastError, toastSuccess]
  );

  // Auto-validate on mount if linkId is provided
  useEffect(() => {
    if (linkId) {
      validateLink(linkId);
    }
  }, [linkId, validateLink]);

  return {
    linkData,
    requestData,
    loading: validating || submitting,
    error,
    validating,
    submitting,
    validateLink,
    submitResponse,
  };
};
